(() => {
  'use strict';

  const DOCUMENT_ELEMENT = document.documentElement;
  const READY_CLASS = 'vf-motion-ready';
  const REVEAL_CLASS = 'vf-reveal';
  const VISIBLE_CLASS = 'is-visible';

  const reduceMotionQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  let observer = null;

  /*
   * Main Shopify sections.
   * Header and footer groups are intentionally excluded.
   */
  const sectionSelector = [
    'main .shopify-section',
    'main > section[data-section-id]',
    'main > section[id^="shopify-section"]',
    'main [data-vf-reveal]'
  ].join(',');

  /*
   * Cards that should reveal in a subtle sequence.
   */
  const cardSelector = [
    'main .product-grid__item',
    'main .collection-list__item',
    'main .blog-articles__article',
    'main .multicolumn-card',
    'main .article-card',
    'main .card-wrapper'
  ].join(',');

  /*
   * Elements that must never receive global animation.
   */
  const excludedSelector = [
    '[data-vf-no-reveal]',
    '[data-vfloor-about]',
    'header',
    'footer',
    'dialog',
    '[role="dialog"]',
    '[hidden]',
    '.drawer',
    '.cart-drawer',
    '.menu-drawer',
    '.predictive-search',
    '.quick-add-modal',
    '.shopify-section-group-header-group',
    '.shopify-section-group-footer-group'
  ].join(',');

  function isExcluded(element) {
    if (!(element instanceof Element)) return true;

    if (element.matches(excludedSelector)) return true;

    if (element.closest('[data-vf-no-reveal]')) return true;
    if (element.closest('[data-vfloor-about]')) return true;
    if (element.closest('header')) return true;
    if (element.closest('footer')) return true;
    if (element.closest('dialog')) return true;
    if (element.closest('[role="dialog"]')) return true;
    if (element.closest('.drawer')) return true;
    if (element.closest('.cart-drawer')) return true;
    if (element.closest('.menu-drawer')) return true;
    if (element.closest('.predictive-search')) return true;

    return false;
  }

  function isActuallyVisible(element) {
    if (!(element instanceof Element)) return false;

    const styles = window.getComputedStyle(element);

    if (styles.display === 'none') return false;
    if (styles.visibility === 'hidden') return false;

    return element.getClientRects().length > 0;
  }

  function getDelayIndex(element) {
    const parent = element.parentElement;

    if (!parent) return 0;

    const visibleSiblings = Array.from(parent.children).filter((child) => {
      return (
        child instanceof Element &&
        child.matches(cardSelector) &&
        !isExcluded(child)
      );
    });

    const index = visibleSiblings.indexOf(element);

    if (index < 0) return 0;

    /*
     * Limit the stagger so large product grids do not feel slow.
     */
    return index % 6;
  }

  function setRevealType(element) {
    const requestedType = element.getAttribute('data-vf-reveal');

    if (requestedType === 'left') {
      element.classList.add('vf-reveal--left');
    }

    if (requestedType === 'right') {
      element.classList.add('vf-reveal--right');
    }

    if (requestedType === 'image') {
      element.classList.add('vf-reveal--image');
    }

    if (element.matches(cardSelector)) {
      element.classList.add('vf-reveal--card');
    }
  }

  function setRevealDelay(element) {
    const manualDelay = element.getAttribute('data-vf-delay');

    if (manualDelay !== null) {
      const parsedDelay = Number.parseInt(manualDelay, 10);
      const safeDelay = Number.isNaN(parsedDelay)
        ? 0
        : Math.min(Math.max(parsedDelay, 0), 8);

      element.style.setProperty(
        '--vf-reveal-delay',
        `${safeDelay * 90}ms`
      );

      return;
    }

    if (element.matches(cardSelector)) {
      const delayIndex = getDelayIndex(element);

      element.style.setProperty(
        '--vf-reveal-delay',
        `${delayIndex * 85}ms`
      );
    }
  }

  function showImmediately(element) {
    element.classList.add(VISIBLE_CLASS);
  }

  function observeElement(element) {
    if (!(element instanceof Element)) return;
    if (element.dataset.vfRevealInitialised === 'true') return;
    if (isExcluded(element)) return;
    if (!isActuallyVisible(element)) return;

    element.dataset.vfRevealInitialised = 'true';
    element.classList.add(REVEAL_CLASS);

    setRevealType(element);
    setRevealDelay(element);

    if (reduceMotionQuery.matches || !observer) {
      showImmediately(element);
      return;
    }

    /*
     * Elements already well inside the viewport reveal immediately,
     * avoiding an unnecessary flash on initial page load.
     */
    const rect = element.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;

    if (rect.top < viewportHeight * 0.88 && rect.bottom > 0) {
      window.requestAnimationFrame(() => {
        showImmediately(element);
      });

      return;
    }

    observer.observe(element);
  }

  function collectElements(root = document) {
    const elements = new Set();

    if (root instanceof Element) {
      if (root.matches(sectionSelector)) {
        elements.add(root);
      }

      if (root.matches(cardSelector)) {
        elements.add(root);
      }
    }

    if (
      root instanceof Document ||
      root instanceof Element ||
      root instanceof DocumentFragment
    ) {
      root.querySelectorAll(sectionSelector).forEach((element) => {
        elements.add(element);
      });

      root.querySelectorAll(cardSelector).forEach((element) => {
        elements.add(element);
      });
    }

    return Array.from(elements);
  }

  function registerElements(root = document) {
    const elements = collectElements(root);

    /*
     * Register sections first, then cards.
     */
    elements
      .sort((first, second) => {
        const firstIsCard = first.matches(cardSelector) ? 1 : 0;
        const secondIsCard = second.matches(cardSelector) ? 1 : 0;

        return firstIsCard - secondIsCard;
      })
      .forEach(observeElement);
  }

  function createObserver() {
    if (
      reduceMotionQuery.matches ||
      !('IntersectionObserver' in window)
    ) {
      return null;
    }

    return new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add(VISIBLE_CLASS);
          currentObserver.unobserve(entry.target);
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -8% 0px',
        threshold: 0.08
      }
    );
  }

  function initialiseMotion() {
    observer = createObserver();

    /*
     * Add animation classes before activating the CSS system.
     * This reduces initial flashes.
     */
    registerElements(document);
    DOCUMENT_ELEMENT.classList.add(READY_CLASS);
  }

  function handleDynamicContent() {
    if (!('MutationObserver' in window)) return;

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;

          registerElements(node);
        });
      });
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function handleShopifyThemeEditor() {
    document.addEventListener('shopify:section:load', (event) => {
      registerElements(event.target);
    });

    document.addEventListener('shopify:block:select', (event) => {
      const block = event.target;

      if (!(block instanceof Element)) return;

      block.classList.add(VISIBLE_CLASS);
    });
  }

  function handleMotionPreferenceChange(event) {
    if (!event.matches) return;

    document.querySelectorAll(`.${REVEAL_CLASS}`).forEach((element) => {
      element.classList.add(VISIBLE_CLASS);

      if (observer) {
        observer.unobserve(element);
      }
    });
  }

  function start() {
    initialiseMotion();
    handleDynamicContent();
    handleShopifyThemeEditor();

    if (typeof reduceMotionQuery.addEventListener === 'function') {
      reduceMotionQuery.addEventListener(
        'change',
        handleMotionPreferenceChange
      );
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {
      once: true
    });
  } else {
    start();
  }
})();
